import React, { useEffect, useRef, useState } from "react";
import { Animated } from "react-animated-css";
import { Button, DropdownButton, Dropdown, ButtonToolbar } from "react-bootstrap";
import DataTable from "react-data-table-component";
import ReactPaginate from "react-paginate";
import { debounce } from "lodash";
import * as Api from "../../../General/api";
import * as Common from "../../../General/common";
import {
  Card,
  CardBody,
  CardHeader,
} from "../../../../_metronic/_partials/controls/Card";
import ModalLoading from "../../workqueue/Modal/ModalLoading";
import FacilityDashboard from "./facilityDashboard";
import PopupModal from "../../modal/popupmodal";

function MyFacilitiesList() {
  const [items, setItems] = useState([]);
  const [totalRows, setTotalRows] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [searchTerm, setSearchTerm] = useState("");
  const [sortColumn, setSortColumn] = useState("FacilityName");
  const [sortDirection, setSortDirection] = useState("asc");
  const [isLoading, setIsLoading] = useState(false);

  const [selectedFacility, setSelectedFacility] = useState(null);

  var facilityDashboardModal = null;

  useEffect(() => {
    fetchFacilities();
  }, [currentPage, rowsPerPage, sortColumn, sortDirection]);

  const fetchFacilities = (search = searchTerm) => {
    setIsLoading(true);
    const qry =
      `?search=${encodeURIComponent(search || "")}` +
      `&sortColumn=${sortColumn}` +
      `&sortDirection=${sortDirection}` +
      `&pageIndex=${currentPage}` +
      `&pageSize=${rowsPerPage}`;
    Api.getRequestQry(Api.Uri_CmnFacilityList, qry)
      .then((res) => {
        if (res.data.statusCode === 200 && res.data.result) {
          setTotalRows(res.data.result.totalRows || 0);
          setItems(res.data.result.items || []);
        } else {
          setItems([]);
          setTotalRows(0);
        }
      })
      .catch((ex) => Common.LogError(ex, "MyFacilitiesList", "fetchFacilities"))
      .finally(() => setIsLoading(false));
  };

  const handleFilter = debounce((val) => {
    setCurrentPage(1);
    fetchFacilities(val);
  }, 800);

  const handleSort = (column, direction) => {
    setSortColumn(column.sortColumn || "FacilityName");
    setSortDirection(direction);
    setCurrentPage(1);
  };

  const handlePagination = (page) => setCurrentPage(page.selected + 1);

  const handlePerPage = (e) => {
    setRowsPerPage(Number(e.target.value));
    setCurrentPage(1);
  };

  const openFacilityDashboard = (facility) => {
    setSelectedFacility(facility);
    setTimeout(() => {
      if (facilityDashboardModal) facilityDashboardModal.showModal();
    }, 100);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "--";
    const d = new Date(dateStr);
    if (isNaN(d)) return "--";
    return `${d.getMonth() + 1}/${d.getDate()}/${d.getFullYear()}`;
  };

  const formatDollar = (amount) => {
    if (amount == null) return "--";
    return `$${Number(amount).toFixed(2)}`;
  };

  const columns = [
    {
      name: "Facility Name",
      sortable: true,
      sortColumn: "FacilityName",
      minWidth: "200px",
      selector: (row) => row.facilityName,
      cell: (row) => (
        <button
          type="button"
          className="btn btn-link p-0 text-left"
          onClick={() => openFacilityDashboard(row)}
        >
          {row.facilityName}
        </button>
      ),
    },
    {
      name: "Ownership-Headquarters",
      sortable: true,
      sortColumn: "OwnershipHeadquartersPurchasingGroupChain",
      minWidth: "220px",
      selector: (row) =>
        Common.replaceNullOrEmptyWithString(row.ownershipHeadquartersPurchasingGroupChain, "--"),
    },
    {
      name: "Primary Ship Date",
      sortable: true,
      sortColumn: "PrimaryShipDate",
      minWidth: "150px",
      selector: (row) => row.primaryShipDate,
      cell: (row) => formatDate(row.primaryShipDate),
    },
    {
      name: "Clinician",
      sortable: true,
      sortColumn: "ClinicianName",
      minWidth: "150px",
      selector: (row) => Common.replaceNullOrEmptyWithString(row.clinicianName, "--"),
    },
    {
      name: "Pending CMN",
      sortable: true,
      sortColumn: "PendingCMNPendingCount",
      minWidth: "130px",
      selector: (row) => row.pendingCMNDisplay,
      cell: (row) => (
        <span title={`${row.pendingCMNPendingCount} pending of ${row.pendingCMNTotalCount} total`}>
          {row.pendingCMNDisplay || "0/0"}
        </span>
      ),
    },
    {
      name: "Dollar Amount",
      sortable: true,
      sortColumn: "DollarAmount",
      minWidth: "130px",
      selector: (row) => row.dollarAmount,
      cell: (row) => (
        <span title="Dollar amount will be populated in a future update">
          {formatDollar(row.dollarAmount)}
        </span>
      ),
    },
    {
      name: "Action",
      allowOverflow: true,
      minWidth: "100px",
      cell: (row) => (
        <ButtonToolbar className="justify-content-center">
          <div className="mr-md-2 mr-0">
            {[DropdownButton].map((DropdownType, idx) => (
              <DropdownType
                size="xs"
                variant="primary"
                title="Action"
                id={`cmn-facility-action-${idx}-${row.facilityId}`}
                key={idx}
              >
                <Dropdown.Item onClick={() => openFacilityDashboard(row)}>
                  View Dashboard
                </Dropdown.Item>
              </DropdownType>
            ))}
          </div>
        </ButtonToolbar>
      ),
    },
  ];

  const CustomPagination = () => {
    const count = Math.ceil(totalRows / rowsPerPage) || 1;
    return (
      <ReactPaginate
        previousLabel=""
        nextLabel=""
        pageCount={count}
        activeClassName="active"
        forcePage={currentPage !== 0 ? currentPage - 1 : 0}
        onPageChange={handlePagination}
        pageClassName="page-item"
        nextLinkClassName="page-link"
        nextClassName="page-item next"
        previousClassName="page-item prev"
        previousLinkClassName="page-link"
        pageLinkClassName="page-link"
        containerClassName="pagination react-paginate justify-content-end my-2 pe-1"
      />
    );
  };

  return (
    <>
      <Animated animationIn="fadeIn" animationInDuration={500}>
        <Card className="example example-compact">
          <CardHeader title="My Facilities" />
          <CardBody>
            <div className="react-dataTable">
              <DataTable
                noHeader
                striped
                highlightOnHover
                responsive
                pagination
                paginationServer
                sortServer
                subHeader
                columns={columns}
                data={items}
                onSort={handleSort}
                progressPending={isLoading}
                progressComponent={<ModalLoading />}
                paginationComponent={CustomPagination}
                className="react-dataTable"
                subHeaderComponent={
                  <div className="w-100 mb-2">
                    <div className="d-flex align-items-center flex-wrap" style={{ gap: "1rem" }}>
                      <div className="d-flex align-items-center">
                        <label className="mb-0 mr-2" htmlFor="fac-search">
                          Search by facility:
                        </label>
                        <input
                          id="fac-search"
                          type="text"
                          className="form-control form-control-sm"
                          style={{ width: "220px" }}
                          placeholder="Facility name..."
                          value={searchTerm}
                          onChange={(e) => {
                            setSearchTerm(e.target.value);
                            handleFilter(e.target.value);
                          }}
                        />
                      </div>
                      <div className="d-flex align-items-center">
                        <label className="mb-0 mr-2" htmlFor="fac-per-page">
                          Show
                        </label>
                        <select
                          id="fac-per-page"
                          className="form-control form-control-sm"
                          style={{ width: "80px" }}
                          value={rowsPerPage}
                          onChange={handlePerPage}
                        >
                          <option value={10}>10</option>
                          <option value={25}>25</option>
                          <option value={50}>50</option>
                          <option value={100}>100</option>
                        </select>
                        <span className="ml-2 text-muted">entries</span>
                      </div>
                    </div>
                  </div>
                }
              />
            </div>
          </CardBody>
        </Card>
      </Animated>

      {/* Facility Dashboard PopupModal */}
      <PopupModal
        size="xl"
        dialogClassName="modal-90w"
        title={
          selectedFacility
            ? `Facility: ${selectedFacility.facilityName}`
            : "Facility Dashboard"
        }
        showButton={false}
        ref={(ref) => {
          facilityDashboardModal = ref;
        }}
      >
        {selectedFacility && (
          <FacilityDashboard
            facilityId={selectedFacility.facilityId}
            facilityName={selectedFacility.facilityName}
          />
        )}
      </PopupModal>
    </>
  );
}

export default MyFacilitiesList;
